import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api } from '../services/api'
import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { useAnalyticsPeriod } from '../lib/analytics-period'

interface Downvote {
  message_id: string
  session_id: string
  session_title: string
  user_id: string
  user_name: string
  user_email: string
  user_question: string
  ai_response: string
  feedback: string
  voted_at: string
  message_created_at: string
}

export default function Downvotes() {
  const { period } = useAnalyticsPeriod()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')

  const { data: downvotes, isLoading, error, refetch } = useQuery<Downvote[]>({
    queryKey: ['downvotes', period],
    queryFn: () => api.getDownvotes(period),
    staleTime: 30000,
  })

  const exportForClaudeCode = () => {
    if (!downvotes || downvotes.length === 0) return

    const exportData = {
      export_date: new Date().toISOString(),
      total_downvotes: downvotes.length,
      purpose: "Analysis for Claude Code to identify improvement areas",
      downvoted_interactions: downvotes.map((dv, index) => ({
        interaction_number: index + 1,
        session_context: {
          session_id: dv.session_id,
          session_title: dv.session_title,
          user_name: dv.user_name,
        },
        conversation: {
          user_question: dv.user_question,
          ai_response: dv.ai_response,
        },
        feedback: {
          feedback_category: dv.feedback,
          voted_at: dv.voted_at,
        },
      }))
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `downvotes-analysis-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const exportAsMarkdown = () => {
    if (!downvotes || downvotes.length === 0) return

    let markdown = `# Downvoted Messages Analysis\n\n`
    markdown += `**Export Date:** ${new Date().toISOString()}\n`
    markdown += `**Total Downvotes:** ${downvotes.length}\n\n`
    markdown += `## Purpose\n\nThis document contains all downvoted AI responses for analysis and improvement.\n\n`
    markdown += `---\n\n`

    downvotes.forEach((dv, index) => {
      markdown += `## Interaction ${index + 1}\n\n`
      markdown += `**Session:** ${dv.session_title || 'Untitled'}\n`
      markdown += `**User:** ${dv.user_name || 'Unknown'}\n`
      markdown += `**Feedback Category:** ${dv.feedback || 'Not specified'}\n`
      markdown += `**Date:** ${new Date(dv.voted_at).toLocaleDateString()}\n\n`
      markdown += `### User Question\n\`\`\`\n${dv.user_question || 'N/A'}\n\`\`\`\n\n`
      markdown += `### AI Response\n\`\`\`\n${dv.ai_response}\n\`\`\`\n\n`
      markdown += `---\n\n`
    })

    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `downvotes-analysis-${new Date().toISOString().split('T')[0]}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const filteredDownvotes = downvotes?.filter(dv =>
    dv.user_question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dv.ai_response?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dv.feedback?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dv.session_title?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="p-8">
        <h1 className="page-title mb-6">Downvoted Messages</h1>
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="card bg-base-100 shadow-xl max-w-md border border-base-300">
          <div className="card-body items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-error mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="card-title text-base-content">Failed to load downvotes</h2>
            <p className="text-base-content opacity-80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <div className="card-actions mt-4">
              <button className="btn btn-primary" onClick={() => refetch()}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="page-title">Downvoted Messages</h1>
          <p className="text-sm opacity-70 mt-1">
            Analyze negative feedback to improve AI responses
          </p>
        </div>
        <div className="badge badge-lg badge-error">
          {filteredDownvotes?.length || 0} Downvotes
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          placeholder="Search questions, responses, or feedback..."
          className="input input-bordered flex-1"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="dropdown dropdown-end">
          <label tabIndex={0} className="btn btn-primary">
            Export
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 ml-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </label>
          <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52 mt-2">
            <li><a onClick={exportForClaudeCode}>JSON (for Claude Code)</a></li>
            <li><a onClick={exportAsMarkdown}>Markdown</a></li>
          </ul>
        </div>
      </div>

      {/* Downvotes List */}
      <div className="space-y-4">
        {filteredDownvotes && filteredDownvotes.length > 0 ? (
          filteredDownvotes.map((dv) => (
            <div key={dv.message_id} className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-lg">{dv.session_title || 'Untitled Session'}</h3>
                    <div className="text-sm opacity-70">
                      {dv.user_name} • {formatDistanceToNow(new Date(dv.voted_at), { addSuffix: true })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {dv.feedback && (
                      <div className="badge badge-error badge-lg">{dv.feedback}</div>
                    )}
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => navigate(`/sessions/${dv.session_id}`)}
                    >
                      View Session
                    </button>
                  </div>
                </div>

                <div className="divider my-2"></div>

                {/* User Question */}
                <div className="mb-4">
                  <div className="text-sm font-semibold opacity-70 mb-2">User Question:</div>
                  <div className="bg-base-300 p-3 rounded-lg">
                    {dv.user_question || 'No question recorded'}
                  </div>
                </div>

                {/* AI Response */}
                <div>
                  <div className="text-sm font-semibold opacity-70 mb-2">AI Response (Downvoted):</div>
                  <div className="bg-base-300 p-3 rounded-lg max-h-48 overflow-y-auto">
                    {dv.ai_response}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="card bg-base-200 shadow-xl">
            <div className="card-body text-center py-12">
              <p className="opacity-60">
                {searchTerm ? 'No downvotes match your search' : 'No downvotes found'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
